// Modules
import { useState } from "react"
import { useMutation } from "react-query"

// Constants
import config from "~constants/config"

// Context
import { useAuthContext } from "~context/authContext"

// Components
import RequestCreationModal from "~components/RequestCreationModal"

// Services
import CommunicationService from "~services/CommunicationService"

// Interfaces
import { MessageShape } from "~shapes/message"
import { OnRequestCloseProps } from "~shapes/modal"
import { ContactUsPayloadShape } from "~shapes/contact-us"

interface MessageRequestModalProps {
  onRequestClose: (props: OnRequestCloseProps) => void
  message?: MessageShape
}

interface FormDataShape {
  subject: string
  message: string
}

const MessageRequestModal = ({
  onRequestClose,
  message,
}: MessageRequestModalProps) => {
  const { loggedUser, isLoading: authLoading } = useAuthContext()

  // Don't render if still loading or user is not loaded yet
  if (authLoading || !loggedUser) {
    return null
  }

  const { isLoading, isSuccess, mutateAsync } = useMutation(
    "contact-us",
    (payload: ContactUsPayloadShape) =>
      CommunicationService.postContactUs(payload)
  )

  const [errorMessage, setErrorMessage] = useState<string>(null!)

  const handleSave = async (formData: FormDataShape) => {
    let messageBody: string

    if (message) {
      messageBody = `Post Link: ${config.APP_MAIN_URL}/app/messages/${message.id}. Message: \n${formData.message}`
    } else {
      messageBody = formData.message
    }

    const payload: ContactUsPayloadShape = {
      to: {
        name: config.APP_NAME,
        mail: config.source_email,
      },
      from: {
        name: config.APP_NAME,
        mail: config.source_email,
      },
      replyTo: {
        name: loggedUser.name,
        mail: loggedUser.userName,
      },
      subject: `Message via Perk Central - ${
        !!message ? "Update" : "New"
      } Message`,
      type: "CONTACT_US",
      parameters: {
        from: loggedUser.name,
        type: formData.subject,
        body: messageBody,
      },
      isHtml: true,
    }

    try {
      await mutateAsync(payload)
    } catch (error) {
      setErrorMessage(error.message)
    }
  }

  return (
    <RequestCreationModal
      isLoading={isLoading}
      onSuccess={isSuccess}
      modalText={
        !message
          ? "To create a new message, contact the Stockperks Administrator team"
          : "To edit this message, contact the Stockperks Administrator team"
      }
      modalTitle={!message ? "Create new message" : "Edit message"}
      errorMessage={errorMessage}
      handleSave={handleSave}
      onRequestClose={onRequestClose}
    />
  )
}

export default MessageRequestModal
